import React, { useState } from "react";
import styled from "styled-components";

const GalleryContainer = styled.div`
  background-color: white;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const GalleryTitle = styled.h3`
  text-align: center;
  margin-bottom: 20px;
  color: #333;
`;

const CreativesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
`;

const CreativeCard = styled.div`
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const CreativePreview = styled.div`
  width: 100%;
  height: 200px;
  background-color: #f5f5f5;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const CreativeInfo = styled.div`
  padding: 15px;
`;

const CreativeName = styled.h4`
  margin: 0 0 10px 0;
  font-size: 16px;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CreativeStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
`;

const StatLabel = styled.span`
  color: #666;
`;

const StatValue = styled.span`
  font-weight: bold;
  color: #333;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  max-width: 90%;
  max-height: 90%;
  overflow: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #333;
`;

function CreativeGallery({ data, selectedKPI }) {
  const [selectedCreative, setSelectedCreative] = useState(null);

  // Fonctions de formatage
  const formatNumber = (value) => {
    return value.toLocaleString("fr-FR");
  };

  const formatCurrency = (value) => {
    return value.toLocaleString("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Mise à jour de la fonction pour obtenir les données des créatifs
  const getCreativesData = () => {
    // Regrouper les données par créatif
    const creativesMap = {};

    data.forEach((item) => {
      const creativeId = item.creativeId || "";
      const creativeName =
        item.creativeName || item.creative || `Creative ${creativeId}`;

      if (!creativesMap[creativeId]) {
        creativesMap[creativeId] = {
          id: creativeId,
          name: creativeName,
          size: item.size || "Unknown",
          impressions: 0,
          clicks: 0,
          budget: 0,
          viewableImpressions: 0,
          measurableImpressions: 0,
          // Stocker l'URL de l'image si disponible
          imageUrl: item.mediaUrl || item.imageUrl || null,
        };
      }

      // Accumuler les métriques
      creativesMap[creativeId].impressions += Number(item.impressions || 0);
      creativesMap[creativeId].clicks += Number(item.clicks || 0);
      creativesMap[creativeId].budget += Number(item.budget || 0);
      creativesMap[creativeId].viewableImpressions += Number(
        item.viewableImpressions || 0
      );
      creativesMap[creativeId].measurableImpressions += Number(
        item.measurableImpressions || 0
      );
    });

    // Calculer les métriques dérivées
    Object.values(creativesMap).forEach((creative) => {
      creative.ctr =
        creative.impressions > 0
          ? (creative.clicks / creative.impressions) * 100
          : 0;
      creative.cpm =
        creative.impressions > 0
          ? (creative.budget / creative.impressions) * 1000
          : 0;
      creative.viewability =
        creative.measurableImpressions > 0
          ? (creative.viewableImpressions / creative.measurableImpressions) *
            100
          : 0;

      // Déterminer la performance pour le KPI sélectionné
      if (selectedKPI === "impressions") {
        creative.performance = creative.impressions;
      } else if (selectedKPI === "clicks") {
        creative.performance = creative.clicks;
      } else if (selectedKPI === "ctr") {
        creative.performance = creative.ctr;
      } else if (selectedKPI === "budget") {
        creative.performance = creative.budget;
      } else if (selectedKPI === "viewability") {
        creative.performance = creative.viewability;
      }
    });

    // Convertir en tableau et trier par performance
    return Object.values(creativesMap).sort(
      (a, b) => b.performance - a.performance
    );
  };

  // Obtenir les données des créatifs
  const creativeData = getCreativesData();

  const handleCreativeClick = (creative) => {
    setSelectedCreative(creative);
  };

  const closeModal = () => {
    setSelectedCreative(null);
  };

  return (
    <GalleryContainer>
      <GalleryTitle>Galerie des bannières</GalleryTitle>

      <CreativesGrid>
        {creativeData.map((creative) => (
          <CreativeCard key={creative.id}>
            <CreativePreview onClick={() => handleCreativeClick(creative)}>
              <iframe
                title={creative.name || `Bannière #${creative.id}`}
                src={`https://ib.adnxs.com/cr?id=${creative.id}`}
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                style={{
                  pointerEvents: "none", // Empêche l'interaction avec l'iframe
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  display: "flex",
                  margin: "auto",
                  transform: "scale(1)",
                  transformOrigin: "center center",
                }}
                onError={(e) => {
                  e.target.srcdoc = `
                    <html>
                      <body style="display:flex;justify-content:center;align-items:center;height:100%;margin:0;background:#f5f5f5;color:#666;font-family:sans-serif;">
                        <p>Aperçu non disponible</p>
                      </body>
                    </html>
                  `;
                }}
              />
            </CreativePreview>

            <CreativeInfo>
              <CreativeName title={creative.name || `Bannière #${creative.id}`}>
                {creative.name || `Bannière #${creative.id}`}
              </CreativeName>
              <CreativeStats>
                <StatItem>
                  <StatLabel>Format:</StatLabel>
                  <StatValue>{creative.size}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Impressions:</StatLabel>
                  <StatValue>{formatNumber(creative.impressions)}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Clics:</StatLabel>
                  <StatValue>{formatNumber(creative.clicks)}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>CTR:</StatLabel>
                  <StatValue>{creative.ctr.toFixed(2)}%</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Budget:</StatLabel>
                  <StatValue>{formatCurrency(creative.budget)}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Viewability:</StatLabel>
                  <StatValue>{creative.viewability.toFixed(2)}%</StatValue>
                </StatItem>
              </CreativeStats>
            </CreativeInfo>
          </CreativeCard>
        ))}
      </CreativesGrid>

      {selectedCreative && (
        <ModalOverlay onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeModal}>×</CloseButton>
            <h3
              title={
                selectedCreative.name || `Bannière #${selectedCreative.id}`
              }
            >
              {selectedCreative.name || `Bannière #${selectedCreative.id}`}
            </h3>
            <div
              style={{ textAlign: "center", margin: "20px 0", height: "500px" }}
            >
              <iframe
                title={
                  selectedCreative.name || `Bannière #${selectedCreative.id}`
                }
                src={`https://ib.adnxs.com/cr?id=${selectedCreative.id}`}
                width="100%"
                height="100%"
                style={{
                  objectFit: "contain",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  pointerEvents: "none", // Empêche l'interaction avec l'iframe
                }}
                frameBorder="0"
                scrolling="no"
                onError={(e) => {
                  e.target.srcdoc = `
                    <html>
                      <body style="display:flex;justify-content:center;align-items:center;height:100%;margin:0;background:#f5f5f5;color:#666;font-family:sans-serif;">
                        <p>Aperçu non disponible</p>
                      </body>
                    </html>
                  `;
                }}
              />
            </div>
            <div>
              <h4>Statistiques détaillées</h4>
              <CreativeStats>
                <StatItem>
                  <StatLabel>Format:</StatLabel>
                  <StatValue>{selectedCreative.size}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Impressions:</StatLabel>
                  <StatValue>
                    {formatNumber(selectedCreative.impressions)}
                  </StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Impressions visibles:</StatLabel>
                  <StatValue>
                    {formatNumber(selectedCreative.viewableImpressions)}
                  </StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Clics:</StatLabel>
                  <StatValue>{formatNumber(selectedCreative.clicks)}</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>CTR:</StatLabel>
                  <StatValue>{selectedCreative.ctr.toFixed(2)}%</StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Budget:</StatLabel>
                  <StatValue>
                    {formatCurrency(selectedCreative.budget)}
                  </StatValue>
                </StatItem>
                <StatItem>
                  <StatLabel>Viewability:</StatLabel>
                  <StatValue>
                    {selectedCreative.viewability.toFixed(2)}%
                  </StatValue>
                </StatItem>
              </CreativeStats>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </GalleryContainer>
  );
}

export default CreativeGallery;
